import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Button, IconButton, Tooltip} from '@mui/material';
import PropTypes from 'prop-types';
import {accountIsBlankOrUnknown, ezprOrange} from '../../_helpers';
import {AccountLine} from './AccountLine.jsx';
import AddIcon from '@mui/icons-material/Add';
import PercentIcon from '@mui/icons-material/Percent';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import InboxIcon from '@mui/icons-material/Inbox';
import {requestStores} from '../../ListOfPREditors.brick.jsx';
import produce from 'immer';
import {AccountDetailsController} from '../../../controllers/AccountDetailsController';
import {materialTypesForEnablingBlankAccountType} from '../../../utils/constants';
import {isEnabledBlankAccountType} from '../../../utils/accountDetails';
import {TablePaginationWithPageSelector} from '../../TablePaginationWithPageSelector.jsx';
import {CheckboxWithOptionsAccountLines} from './CheckboxWithOptionsAccountLines.jsx';
import Snackbar from '@mui/material/Snackbar';
import CloseIcon from '@mui/icons-material/Close';
import {BrickContext} from 'olympe';
import {useFieldEditability} from '../utils/useFieldEditability.js';


/**
 * @param {AccountLines.propTypes} props
 * @return {JSX.Element}
 */
export function AccountLines(props) {
    const useRequestStore = requestStores.get(props.prTag);
    const {isDisabled} = useFieldEditability(props.prTag, {context: props.context});

    const [accounts, setOneAccount] = useRequestStore((state) => [state.accounts, state.setOneAccount]);
    const [accountDetailsByMaterial, setAccountDetailsByMaterial] = useRequestStore((state) => [state.accountDetailsByMaterial, state.setAccountDetailsByMaterial]);
    const setAllAccounts = useRequestStore((state) => state.setAllAccounts);
    const createAccount = useRequestStore((state) => state.createAccount);
    const deleteAccountDetailsByMaterial = useRequestStore((state) => state.deleteAccountDetailsByMaterial);
    const materials = useRequestStore((state) => state.materials);
    const [areAccountSplitByMaterial, setAreAccountSplitByMaterial] = useRequestStore((state) => [state.areAccountSplitByMaterial, state.setAreAccountSplitByMaterial]);
    const [sliderWrongAmountTooltipText, setSliderWrongAmountTooltipText] = useState('');
    const purchasingGroup = useRequestStore((state) => state.purchasingGroup);
    const plant = useRequestStore((state) => state.defaultPlant);
    const vendorCode = useRequestStore((state) => state.vendorCode);
    const wayOfBuying = useRequestStore((state) => state.wayOfBuying.currentValue);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pageNum, setPageNum] = useState([]);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [pendingUpdate, setPendingUpdate] = useState(null);
    const [allocationTotalError, setAllocationTotalError] = useState(false);

    const [getTotalMaterialPriceWithoutCurrencyExchange, getTotalMaterialQuantity, getTotalPriceWithoutCurrencyExchange, getTotalQuantity] = useRequestStore((state) => {
        return [
            state.getTotalMaterialPriceWithoutCurrencyExchange,
            state.getTotalMaterialQuantity,
            state.getTotalPriceWithoutCurrencyExchange,
            state.getTotalQuantity,
        ];
    });

    const accountsUsed = useMemo(() => {
        return areAccountSplitByMaterial ?
            accountDetailsByMaterial
                .filter((a) => a.material_id !== undefined)
                .find((a) => a.material_id === props.materialId)?.accounts || [] :
            accounts;
    }, [
        areAccountSplitByMaterial,
        accountDetailsByMaterial,
        props.materialId,
        accounts,
    ]);

    const pageSize = useMemo(
        () =>
            rowsPerPage === -1 ?
                accountsUsed.length :
                accountsUsed.length / rowsPerPage,
        [accountsUsed.length, rowsPerPage],
    );


    const canSplitAccounts = wayOfBuying.value !== 'FO';
    const percentageOnlyAllocation = !areAccountSplitByMaterial && materials.length > 1;

    // The account type and allocation type are the same for all account in an account lines.
    const accountType = accountsUsed[0].accountType;
    const allocationType = accountsUsed[0].allocationType;

    const totalPrice = areAccountSplitByMaterial ? getTotalMaterialPriceWithoutCurrencyExchange(props.materialId) : getTotalPriceWithoutCurrencyExchange();
    const totalQuantity = areAccountSplitByMaterial ? getTotalMaterialQuantity(props.materialId) : getTotalQuantity();

    const [allocateForValues, setAllocateForValues] = useState(accountsUsed.map((account) => {
        return {value: Number(account.allocateFor), isFixed: account.isFixed, error: false};
    }));

    const accountsCount = accountsUsed?.length;

    const [total, setTotal] = useState(allocationType === 'percentage' ? 100 :
        allocationType === 'price' ? totalPrice : totalQuantity);

    const isNotBlank = !accountIsBlankOrUnknown(accountType);

    // This useEffect changes the allocation to percentage whenever we have multiple materials, and we are not splitting by material
    useEffect(() => {
        if (percentageOnlyAllocation) {
            if (allocationType !== 'percentage') {
                if (areAccountSplitByMaterial) {
                    const newAccountDetailsByMaterial = produce(accountDetailsByMaterial, (draft) => {
                        draft.find((item) => item.material_id === props.materialId).accounts.forEach((account) => {
                            account.allocationType = 'percentage';
                        });
                    });
                    setAccountDetailsByMaterial(newAccountDetailsByMaterial, props.materialId);
                } else {
                    const newAccounts = produce(accountsUsed, (draft) => {
                        draft.forEach((account) => {
                            account.allocationType = 'percentage';
                        });
                    });
                    setAllAccounts(newAccounts);
                }
                setTotal(100);
            }
        }
    }, [percentageOnlyAllocation]);

    // This useEffect applies the modification on the allocation if the user update it from another account lines
    useEffect(() => {
        if (props.modifiedAllocation?.val && props.modifiedAllocation.mIds.includes(props.materialId)) {
            handleAllocateForChange(props.modifiedAllocation.val, 0, true);
        }
    }, [props.modifiedAllocation]);

    // This useEffect applies the allocation reset if the user resets it from another account lines
    useEffect(() => {
        if (props.resetAllocation?.mIds?.includes(props.materialId)) {
            setAllocateForValues((prevState) => {
                return prevState.map((e) => ({...e, isFixed: false, error: false, disabled: false}));
            });
            setTotal(allocationType === 'percentage' ? 100 : allocationType === 'price' ? totalPrice : totalQuantity);
            updateAllocateForCalculation();
        }
    }, [props.resetAllocation]);

    // Check if the number of allocations for values matches the number of accounts, if not add the difference
    useEffect(() => {
        const difference = accountsUsed.length - allocateForValues.length;

        if (difference > 0) {
            const newAllocations = Array(difference).fill().map(() => ({
                value: 100,
                isFixed: false,
                error: false,
            }));

            setAllocateForValues((prevState) => [...prevState, ...newAllocations]);
            updateAllocateForCalculation();
        } else if (difference < 0) {
            allocateForValues.splice(0, 1);
            updateAllocateForCalculation();
        }
    }, [accountsUsed.length, allocateForValues.length]);


    // This useEffect triggers when allocationType changes and updates total accordingly
    useEffect(() => {
        setTotal(allocationType === 'percentage' ? 100 : allocationType === 'price' ? totalPrice : totalQuantity);
    }, [allocationType]);


    useEffect(() => { // Change value of allocate for fields in all accounts
        updateAllocateFor(allocateForValues);
    }, [allocateForValues]);

    const firstUpdate = useRef(true);
    useEffect(() => { // Change value of allocate for fields in all accounts
        if (firstUpdate.current) {
            firstUpdate.current = false;
            return;
        }

        setAllocateForValues((prevState) => {
            return prevState.map((e) => ({...e, isFixed: false, error: false}));
        });
        updateAllocateForCalculation();
    }, [allocationType]);

    useEffect(() => {
        if (!isNotBlank) {
            setOneAccount();
        }
    }, [isNotBlank, setOneAccount]);

    // This useEffect will tick the "Different payment accounts for each item" checkbox if needed when one of the criteria
    // enabling the Blank account type changed
    useEffect(() => {
        // We check if we have multiple materials with different type, including at least one ZTAS or FERT
        const types = new Set(materials.map((m) => m.type));
        const shouldTickDifferentAccountForEachItem = types.size > 1 &&
            materialTypesForEnablingBlankAccountType.some((mt) => types.has(mt)) &&
            materials.filter((m) => materialTypesForEnablingBlankAccountType.includes(m.type))
                .some((e) => isEnabledBlankAccountType(vendorCode, purchasingGroup.value, e.plant.value, e.type));

        if (shouldTickDifferentAccountForEachItem) {
            setAreAccountSplitByMaterial(shouldTickDifferentAccountForEachItem);
        }
    }, [vendorCode, plant, purchasingGroup, materials]);

    useEffect(() => {
        const allocationSum = allocateForValues.reduce((sum, allocation) => sum + allocation.value, 0);
        const diff = allocationSum - total;
        const absDiff = Math.abs(diff);
        const epsilon = 1e-6;
        if (absDiff > epsilon) {
            setAllocationTotalError(true);
            if (diff > 0) {
                const formattedDiff = formatDiff(diff, allocationType);
                setSliderWrongAmountTooltipText(`Allocation exceeds the amount by ${formattedDiff}. Please adjust.`);
            } else {
                const formattedDiff = formatDiff(diff, allocationType);
                setSliderWrongAmountTooltipText(`Allocation is less than the amount by ${formattedDiff}. Please adjust.`);
            }
        } else {
            setAllocationTotalError(false);
            setSliderWrongAmountTooltipText('');
        }
    }, [allocateForValues, total, allocationType]);

    const formatDiff = (diff, allocationType) => {
        const absDiff = Math.abs(diff);
        if (allocationType === 'percentage') {
            return `${absDiff.toFixed(1)}%`;
        } else if (allocationType === 'price') {
            return `${absDiff.toFixed(2)}`;
        } else if (allocationType === 'quantity') {
            return `${absDiff.toFixed(3)}`;
        }
        return absDiff.toString();
    };

    const updateAllocateForCalculation = (action = null) => {
        setAllocateForValues((prevState) => {
            const temp = [...prevState];
            let totalForCalculation;
            switch (allocationType) {
            case 'price':
                totalForCalculation = totalPrice;
                break;
            case 'quantity':
                totalForCalculation = totalQuantity;
                break;
            default:
                totalForCalculation = total;
            }

            return AccountDetailsController.computeAllocateForValues(
                temp,
                totalForCalculation,
                allocationType,
                allocationTotalError,
                action,
            );
        });
    };

    const updateAllocateFor = (newValues) => {
        if (areAccountSplitByMaterial) {
            const newAccountDetailsByMaterial = produce(accountDetailsByMaterial, (draft) => {
                const accountDetails = draft.find((item) => item.material_id === props.materialId);
                accountDetails.accounts.forEach((account, i) => {
                    account.allocateFor = newValues[i].value;
                    account.isFixed = newValues[i].isFixed;
                });
            });
            setAccountDetailsByMaterial(newAccountDetailsByMaterial, props.materialId);
        } else {
            const newAccounts = produce(accountsUsed, (draft) => {
                draft.forEach((account, i) => {
                    account.allocateFor = newValues[i].value;
                    account.isFixed = newValues[i].isFixed;
                });
            });
            setAllAccounts(newAccounts);
        }
    };

    const handleResetAllocateFor = () => {
        setAllocateForValues((prevState) => {
            const defaultValue = allocationType === 'percentage' ? total / prevState.length :
                allocationType === 'price' ? totalPrice / prevState.length : totalQuantity / prevState.length;

            return prevState.map((e) => ({...e, isFixed: false, error: false, disabled: false, value: defaultValue}));
        });
        setTotal(allocationType === 'percentage' ? 100 : allocationType === 'price' ? totalPrice : totalQuantity);
        updateAllocateForCalculation();

        if (areAccountSplitByMaterial && props.selectedAccounts.length > 0) {
            setPendingUpdate([AccountLines.updateActions.RESET_ALLOCATION]);
            setSnackbarOpen(true);
        }
    };

    const checkAllocateForValues = (idx) => {
        setAllocateForValues((prevState) => {
            const temp = [
                ...prevState,
            ];

            const isTotalIncorrect = AccountDetailsController.checkAllocateForValues(temp, total);

            temp[idx].isFixed = !isTotalIncorrect;
            temp[idx].error = isTotalIncorrect;

            return temp;
        });
    };

    const handleClickAllocateBtn = (allocationType) => {
        if (percentageOnlyAllocation && allocationType !== 'percentage') {
            return;
        }

        if (areAccountSplitByMaterial) {
            const newAccountDetailsByMaterial = produce(accountDetailsByMaterial, (draft) => {
                draft.find((item) => item.material_id === props.materialId).accounts.forEach((account) => account.allocationType = allocationType);
            });
            setAccountDetailsByMaterial(newAccountDetailsByMaterial, props.materialId);
        } else {
            const newAccounts = produce(accountsUsed, (draft) => {
                draft.forEach((account) => account.allocationType = allocationType);
            });
            setAllAccounts(newAccounts);
        }

        setTotal(allocationType === 'percentage' ? 100 : allocationType === 'price' ? totalPrice : totalQuantity);

        if (areAccountSplitByMaterial && props.selectedAccounts.length > 0) {
            setPendingUpdate([AccountLines.updateActions.ALLOCATION_TYPE, allocationType]);
            setSnackbarOpen(true);
        }
    };

    const handleAllocateForChange = (newValue, idx, fromCopy = false) => {
        const newValues = [...allocateForValues];
        // DO NOT EDIT ATTRIBUTES OF THE SHALLOW COPY newValues !
        newValues[idx] = {...newValues[idx], isFixed: true, value: newValue};
        const newAllocateFor = AccountDetailsController.computeAllocateForValues(
            newValues,
            total,
            allocationType,
            allocationTotalError,
        );

        const allocationSum = newAllocateFor.reduce((s, allocateFor) => s + allocateFor.value, 0);
        const diff = allocationSum - total;
        const epsilon = 1e-6;
        const isNewTotalIncorrect = Math.abs(diff) > epsilon || newAllocateFor.some((allocateFor) => allocateFor.value < 0);

        if (isNewTotalIncorrect && !allocationTotalError) {
            setAllocateForValues((prevState) => {
                const temp = [...prevState];
                return temp.map((oldAllocate, idx) => ({
                    ...oldAllocate,
                    value: accountsUsed[idx].allocateFor,
                }));
            });
        } else {
            // Try to fix the modified value
            setAllocateForValues((prevState) => {
                const temp = [...prevState];

                temp[idx].value = newValue;
                temp[idx].isFixed = true;
                temp[idx].error = isNewTotalIncorrect;

                return temp;
            });
            updateAllocateForCalculation();
            checkAllocateForValues(idx);

            setAllocateForValues((prevState) => {
                const temp = [...prevState];

                if (!allocationTotalError) {
                    if (temp.length - temp.reduce((acc, v) => acc + (v.isFixed ? 1 : 0), 0) === 1) {
                        const idxOfUnfixedVal = temp.findIndex((v) => !v.isFixed);
                        temp[idxOfUnfixedVal].disabled = true;
                    } else {
                        temp.forEach((v) => (v.disabled = false));
                    }
                } else {
                    temp.forEach((v) => (v.disabled = false));
                }
                return temp;
            });
        }

        if (areAccountSplitByMaterial && props.selectedAccounts.length > 0 && !fromCopy) {
            setPendingUpdate([AccountLines.updateActions.UPDATE, newValue]);
            setSnackbarOpen(true);
        }
    };

    const deleteAnAllocateForValue = (idx) => {
        setAllocateForValues((prevState) => {
            const temp = [...prevState];
            temp.splice(idx, 1);
            return temp;
        });

        updateAllocateForCalculation('delete');

        if (areAccountSplitByMaterial && props.selectedAccounts.length > 0) {
            setPendingUpdate([AccountLines.updateActions.DELETE]);
            setSnackbarOpen(true);
        }
    };

    const handleAddLine = () => {
        createAccount(props.materialId);
        setAllocateForValues((prevState) => {
            const temp = [
                ...prevState,
            ];
            temp.push({value: 100, isFixed: false, error: false});
            return temp;
        });
        updateAllocateForCalculation('add');

        if (areAccountSplitByMaterial && props.selectedAccounts.length > 0) {
            setPendingUpdate([AccountLines.updateActions.ADD]);
            setSnackbarOpen(true);
        }
    };

    // starting logic for pagination
    useEffect(() => {
        if (rowsPerPage === -1) {
            setPage(0);
        } else {
            if (accountsUsed?.length > 0 && accountsUsed?.length <= page * rowsPerPage) {
                setPage((current) => current - 1);
            }
        }
    }, [accountsUsed, rowsPerPage, page]);

    useEffect(() => {
        const temp = [];
        if (rowsPerPage === -1) {
            temp.push(
                {
                    label: `1-${accountsUsed.length}`,
                    value: 1,
                },
            );
        } else {
            for (let i = 0; i < pageSize; i++) {
                temp.push({
                    label: `${(i * rowsPerPage) + 1}-${(i + 1) * rowsPerPage < accountsUsed.length ? (i + 1) * rowsPerPage : accountsUsed.length}`,
                    value: i + 1,
                });
            }
        }
        return setPageNum(temp);
    }, [pageSize]);

    const handlePageSelection = (newValue) => {
        setPage(newValue);
    };

    const handleChangePage = (_event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };
    // ended logic for pagination

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setSnackbarOpen(false);
    };

    const handleUpdateAction = () => {
        const materialsWithSelectedAccounts = new Set(props.selectedAccounts.map((selected) => selected.materialId));
        materialsWithSelectedAccounts.delete(props.materialId);
        if (pendingUpdate[0] === AccountLines.updateActions.ADD) {
            materialsWithSelectedAccounts.forEach((materialId) => {
                createAccount(materialId);
            });
        } else if (pendingUpdate[0] === AccountLines.updateActions.DELETE) {
            materialsWithSelectedAccounts.forEach((materialId) => {
                deleteAccountDetailsByMaterial(materialId, 0);
            });
        } else if (pendingUpdate[0] === AccountLines.updateActions.UPDATE) {
            const newValue = pendingUpdate[1];

            const materialIdToUpdate = accountDetailsByMaterial
                .filter((materialDetail) => materialsWithSelectedAccounts.has(materialDetail.material_id))
                .map((m) => m.material_id);

            props.handleCopyAllocation(materialIdToUpdate, newValue);
        } else if (pendingUpdate[0] === AccountLines.updateActions.ALLOCATION_TYPE) {
            const allocationType = pendingUpdate[1];

            materialsWithSelectedAccounts.forEach((materialId) => {
                const newAccountDetailsByMaterial = produce(accountDetailsByMaterial, (draft) => {
                    draft.find((item) => item.material_id === materialId).accounts.forEach((account) => account.allocationType = allocationType);
                });
                setAccountDetailsByMaterial(newAccountDetailsByMaterial, materialId);
            });
        } else if (pendingUpdate[0] === AccountLines.updateActions.RESET_ALLOCATION) {
            const materialIdToReset = accountDetailsByMaterial
                .filter((materialDetail) => materialsWithSelectedAccounts.has(materialDetail.material_id))
                .map((m) => m.material_id);

            props.handleResetAllocation(materialIdToReset);
        }
        setPendingUpdate(null);
        setSnackbarOpen(false);
    };

    const snackbarAction = (
        <React.Fragment>
            <Button color="secondary" size="small" onClick={() => handleUpdateAction()}>
                YES
            </Button>
            <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleCloseSnackbar}
            >
                <CloseIcon fontSize="small"/>
            </IconButton>
        </React.Fragment>
    );

    return (
        <div style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
        }} className={'pr-editor-account-details-account-container'}>
            <div className={'pr-editor-account-details-account-line-title'}>
                {(accountsUsed.length > 1 || areAccountSplitByMaterial) && (
                    <CheckboxWithOptionsAccountLines
                        selectedAccounts={props.selectedAccounts}
                        materialId={props.materialId}
                        accountsUsed={accountsUsed}
                        onSelectAllAccounts={props.onSelectAllAccounts}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        disabled={isDisabled('accountLine.selectAll')}
                    />
                )}
                <div className={'pr-editor-account-details-account-line-title-element'}>Account type *</div>
                {
                    (accountType.value === 'P' || accountType.value === 'S') && isNotBlank ?
                        <div className={'pr-editor-account-details-account-line-title-element'}>WBS / Project
                            *</div> : undefined
                }
                {
                    (accountType.value === 'K' || accountType.value === 'S') && isNotBlank ?
                        <div className={'pr-editor-account-details-account-line-title-element'}>Cost Center
                            *</div> : undefined
                }
                {
                    accountType.value === '2' && isNotBlank ?
                        <div className={'pr-editor-account-details-account-line-title-element'}>Profit Center
                            *</div> : undefined
                }
                {
                    isNotBlank ?
                        <div className={'pr-editor-account-details-account-line-title-element'}>G/L number
                            *</div> : undefined
                }
                {
                    canSplitAccounts && isNotBlank ? <div className={'pr-editor-account-details-allocate-for'}>
                        <div className={'pr-editor-account-details-account-line-title-element'}>Allocate for</div>
                        <div className={'pr-editor-account-details-allocate-for-icons'}>
                            <Tooltip title={'Reset and balance equally'}>
                                <span>
                                    <IconButton
                                        aria-label="reset"
                                        size="small"
                                        onClick={handleResetAllocateFor}
                                        sx={{marginRight: '10px'}}
                                        disabled={isDisabled('accountLine.resetAllocation')}>
                                        <SettingsBackupRestoreIcon fontSize="inherit"/>
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title={
                                <>
                                    <div style={{textAlign: 'center'}}>Percentage</div>
                                    <br/>
                                    1 decimal place
                                </>
                            }>
                                <span>
                                    <IconButton
                                        aria-label="percentage"
                                        size="small"
                                        onClick={() => handleClickAllocateBtn('percentage')}
                                        sx={{
                                            'color': allocationType === 'percentage' ?
                                                ezprOrange :
                                                '#00000089',
                                            '&.Mui-disabled': {
                                                color: allocationType === 'percentage' ?
                                                    '#000000' :
                                                    '#00000042',
                                            },
                                        }}
                                        disabled={isDisabled('accountLine.allocatePercentage')}
                                    >
                                        <PercentIcon fontSize="inherit"/>
                                    </IconButton>
                                </span>
                            </Tooltip>
                            {!percentageOnlyAllocation && (
                                <>
                                    <Tooltip title={
                                        <>
                                            <div style={{textAlign: 'center'}}>Amount</div>
                                            <br/>
                                            2 decimal places
                                        </>
                                    }>
                                        <span>
                                            <IconButton
                                                aria-label="amount"
                                                size="small"
                                                onClick={() => handleClickAllocateBtn('price')}
                                                sx={{
                                                    'color': allocationType === 'price' ?
                                                        ezprOrange :
                                                        '#00000089',
                                                    '&.Mui-disabled': {
                                                        color: allocationType === 'price' ?
                                                            ezprOrange :
                                                            '#00000042',
                                                    },
                                                }}
                                                disabled={isDisabled('accountLine.allocateAmount')}
                                            >
                                                <AttachMoneyIcon fontSize="inherit"/>
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={
                                        <>
                                            <div style={{textAlign: 'center'}}>Quantity</div>
                                            <br/>
                                            3 decimal places
                                        </>
                                    }>
                                        <span>
                                            <IconButton
                                                aria-label="quantity"
                                                size="small"
                                                onClick={() => handleClickAllocateBtn('quantity')}
                                                sx={{
                                                    'color': allocationType === 'quantity' ?
                                                        ezprOrange :
                                                        '#00000089',
                                                    '&.Mui-disabled': {
                                                        color: allocationType === 'quantity' ?
                                                            ezprOrange :
                                                            '#00000042',
                                                    },
                                                }}
                                                disabled={isDisabled('accountLine.allocateQuantity')}>
                                                <InboxIcon fontSize="inherit"/>
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </>
                            )}
                        </div>
                    </div> : undefined
                }
                {
                    isNotBlank ?
                        <div className={'pr-editor-account-details-account-line-title-element'}>Status</div> : undefined
                }
                {
                    (accountType.value === 'P') && isNotBlank ?
                        <div className={'pr-editor-account-details-account-line-title-element'}>Expected amount to
                            pay</div> : undefined
                }
                {
                    (accountType.value === 'P') && isNotBlank ?
                        <div className={'pr-editor-account-details-account-line-title-element'}>Exceeded
                            amount</div> : undefined
                }
                {
                    isNotBlank ?
                        <div className={'pr-editor-account-details-account-line-title-element'}>
                            <div style={{width: '40px'}}/>
                        </div> : undefined
                }
            </div>
            {accountsUsed.map((_account, idx) => {
                if (idx >= rowsPerPage * page && idx < rowsPerPage * (page + 1) || rowsPerPage === -1) {
                    return (
                        <AccountLine
                            key={'accountLine' + idx}
                            idx={idx}
                            accountsCount={accountsCount}
                            materialId={props.materialId}
                            allocateForValue={allocateForValues[idx] || []}
                            handleAllocateForChange={handleAllocateForChange}
                            sliderWrongAmountTooltipText={sliderWrongAmountTooltipText}
                            deleteAnAllocateForValue={deleteAnAllocateForValue}
                            total={Number(total)}
                            prTag={props.prTag}
                            context={props.context}
                            consumptionLevelCheckDetails={props.consumptionLevelCheckDetails}
                            consumptionLevelCheckStatus={props.consumptionLevelCheckStatus}
                            selectedAccounts={props.selectedAccounts}
                            onSelectAccount={props.onSelectAccount}
                            updateSelectedAccounts={props.updateSelectedAccounts}
                            handleResetAllocateFor={handleResetAllocateFor}
                            allocationTotalError={allocationTotalError}
                        />
                    );
                }
            })}
            <div className={'pr-editor-account-details-account-action-container'}>
                {
                    canSplitAccounts && (<Button
                        className="pr-editor-account-details-split-btn"
                        startIcon={<AddIcon/>}
                        sx={{
                            color: ezprOrange,
                            borderColor: ezprOrange,
                            border: '2px solid',
                            borderRadius: '20px',
                            marginTop: '0.5rem',
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                        }}
                        onClick={handleAddLine}
                        disabled={isDisabled('accountLine.split')}>
                        {percentageOnlyAllocation ? 'SPLIT BY PERCENTAGE' : 'SPLIT'}
                    </Button>)
                }
                <TablePaginationWithPageSelector
                    page={page}
                    pageNum={pageNum}
                    count={accountsUsed.length}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[2, 5, 10, 20, 50, 100, {label: 'All', value: -1}]}
                    handlePageSelection={handlePageSelection}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </div>
            <Snackbar
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                message={'Do you want to modify all selected account lines?'}
                action={snackbarAction}
            />
        </div>
    );
}

/**
 * @enum {string}
 */
AccountLines.updateActions = {
    ADD: 'addLine',
    DELETE: 'deleteLine',
    UPDATE: 'updateLine',
    ALLOCATION_TYPE: 'allocationType',
    RESET_ALLOCATION: 'resetAllocation',
};

AccountLines.propTypes = {
    prTag: PropTypes.string,
    materialId: PropTypes.number,
    context: PropTypes.instanceOf(BrickContext).isRequired,
    consumptionLevelCheckDetails: PropTypes.array,
    consumptionLevelCheckStatus: PropTypes.string,
    selectedAccounts: PropTypes.array,
    onSelectAccount: PropTypes.func,
    onSelectAllAccounts: PropTypes.func,
    updateSelectedAccounts: PropTypes.func,
    handleCopyAllocation: PropTypes.func,
    modifiedAllocation: PropTypes.object,
    handleResetAllocation: PropTypes.func,
    resetAllocation: PropTypes.object,
};
